import { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { FilterPreviewProps } from './types';
import { applyFilter } from './outputFilterUtils';

const DARK_COLORS = {
  background: '#101828',
  headerBg: '#1D2939',
  text: '#D0D5DD',
  textSecondary: '#667085',
  border: '#344054',
  accent: '#2ED3B7',
};

const LIGHT_COLORS = {
  background: '#F9FAFB',
  headerBg: '#FFFFFF',
  text: '#101828',
  textSecondary: '#667085',
  border: '#EAECF0',
  accent: '#0D9488',
};

export default function FilterPreview({ jsonOutput, selectedPaths, mode, theme }: FilterPreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isDark = theme === 'dark';
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;
  const fieldCount = selectedPaths.length;
  const hasSelection = fieldCount > 0;
  const previewContent = useMemo(() => {
    if (!hasSelection) return '';
    return applyFilter(jsonOutput, { selectedPaths, mode, schemaHash: '' });
  }, [jsonOutput, selectedPaths, mode, hasSelection]);
  const fieldLabel = `${fieldCount} field${fieldCount !== 1 ? 's' : ''}`;
  const indicator = mode === 'raw' ? `${fieldLabel} · raw` : fieldLabel;
  return (
    <Box sx={{ borderRadius: '6px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
      <Box
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: colors.headerBg,
          '&:hover': { opacity: 0.85 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>
            Preview
          </Typography>
          {hasSelection && (
            <Typography sx={{ fontSize: '11px', color: colors.accent, fontWeight: 500 }}>
              {indicator}
            </Typography>
          )}
        </Box>
        {isOpen ? (
          <CaretUp size={14} color={colors.textSecondary} />
        ) : (
          <CaretDown size={14} color={colors.textSecondary} />
        )}
      </Box>
      <Collapse in={isOpen} unmountOnExit>
        <Box
          sx={{
            backgroundColor: colors.background,
            padding: '12px',
            maxHeight: '200px',
            overflowY: 'auto',
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          {hasSelection ? (
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '12px',
                color: colors.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {previewContent}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '12px', color: colors.textSecondary, fontStyle: 'italic' }}>
              Select fields above to see preview
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
